"use client";

import { motion } from "framer-motion";
import {
  LayoutGrid,
  ShoppingBag,
  CreditCard,
  Code2,
  ScrollText,
  Settings,
  LogOut,
  Zap,
  Phone,
  Star,
} from "lucide-react";

const navItems = [
  { label: "Dashboard", icon: LayoutGrid, active: true },
  { label: "Marketplace", icon: ShoppingBag },
  { label: "Subscriptions", icon: CreditCard },
  { label: "API Docs", icon: Code2 },
  { label: "Logs", icon: ScrollText },
  { label: "Settings", icon: Settings },
];

export default function DashboardMockup() {
  return (
    <motion.div
      initial={{ x: 40 }}
      animate={{ x: 0 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className="relative mx-auto w-full max-w-xl pt-8"
    >
      {/* Main dashboard card */}
      <div className="overflow-hidden rounded-2xl border border-black/5 bg-white shadow-2xl shadow-black/10">
        <div className="flex">
          {/* Sidebar */}
          <div className="hidden w-36 shrink-0 flex-col gap-1 border-r border-black/5 bg-cream/60 p-3 sm:flex">
            {navItems.map((item) => (
              <div
                key={item.label}
                className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-[11px] font-medium ${
                  item.active
                    ? "bg-brand text-white"
                    : "text-ink-soft hover:bg-black/5"
                }`}
              >
                <item.icon className="h-3 w-3 shrink-0" />
                <span className="truncate">{item.label}</span>
              </div>
            ))}
            <div className="mt-2 flex items-center gap-2 rounded-md px-2 py-1.5 text-[11px] font-medium text-ink-soft">
              <LogOut className="h-3 w-3" />
              Logout
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 space-y-3 p-4 pt-14">
            {/* Stat row */}
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-lg border border-black/5 bg-white p-2.5 shadow-sm">
                <p className="text-[10px] text-ink-soft">API Calls Today</p>
                <p className="text-sm font-semibold text-ink">8,241</p>
              </div>
              <div className="rounded-lg border border-black/5 bg-white p-2.5 shadow-sm">
                <p className="flex items-center gap-1 text-[10px] text-ink-soft">
                  <Zap className="h-2.5 w-2.5 text-brand" />
                  vs yesterday
                </p>
                <p className="text-sm font-semibold text-emerald-600">
                  +4.3%
                </p>
              </div>
            </div>

            {/* Chart placeholder */}
            <div className="rounded-lg border border-black/5 bg-white p-2.5 shadow-sm">
              <p className="text-[10px] text-ink-soft">API Usage</p>
              <p className="mb-1.5 text-[9px] text-ink-soft/70">
                Monthly API call volume
              </p>
              <div className="flex h-12 items-end gap-1">
                {[40, 65, 50, 80, 55, 90, 60, 75, 45, 85, 70, 95].map(
                  (h, i) => (
                    <motion.div
                      key={i}
                      initial={{ height: 0 }}
                      whileInView={{ height: `${h}%` }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 0.6,
                        delay: 0.5 + i * 0.04,
                        ease: "easeOut",
                      }}
                      className="flex-1 rounded-t-sm bg-brand-light/70"
                    />
                  )
                )}
              </div>
            </div>

            {/* Top dataset */}
            <div className="rounded-lg border border-black/5 bg-white p-2.5 shadow-sm">
              <p className="text-[10px] text-ink-soft">Top Datasets</p>
              <div className="mt-1 flex items-center justify-between">
                <span className="text-[11px] font-medium text-ink">
                  US Healthcare Organizations
                </span>
                <span className="text-[10px] font-semibold text-brand">
                  $99/mo
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating "Current Plan" card — top left */}
      <motion.div
        initial={{ y: 10, scale: 0.95 }}
        animate={{ y: 0, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="absolute -top-4 -left-2 z-20 sm:-top-6 sm:-left-6 lg:-top-8 lg:-left-8"
      >
        <motion.div
          animate={{ y: [0, -8, 0] }}
          transition={{
            duration: 4,
            delay: 1.2,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="w-44 rounded-xl border border-white/60 bg-white/80 p-3 shadow-2xl shadow-black/10 backdrop-blur-xl backdrop-saturate-150 sm:w-56 sm:p-3.5"
        >
          <div className="flex items-center justify-between text-[10px] sm:text-[11px]">
            <span className="text-ink-soft">Current Plan</span>
            <button className="rounded-full bg-white/60 px-2 py-0.5 text-[9px] font-medium text-brand sm:text-[10px]">
              Upgrade
            </button>
          </div>
          <p className="mt-0.5 text-sm font-semibold text-ink sm:mt-1 sm:text-base">
            Pro — $149/mo
          </p>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/50 sm:mt-2.5">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: "72%" }}
              transition={{ duration: 1.2, delay: 0.9, ease: "easeOut" }}
              className="h-full rounded-full bg-gradient-to-r from-brand to-brand-dark"
            />
          </div>
          <div className="mt-1 flex justify-between text-[9px] text-ink-soft sm:mt-1.5 sm:text-[10px]">
            <span>API Calls</span>
            <span>841K / 2M</span>
          </div>
        </motion.div>
      </motion.div>

      {/* Floating call card — bottom left */}
      <motion.div
        initial={{ y: 10 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, delay: 1, ease: "easeOut" }}
        className="absolute bottom-8 -left-3 z-20 sm:bottom-10 sm:-left-6 lg:bottom-12 lg:-left-8"
      >
        <motion.div
          animate={{ y: [0, -6, 0] }}
          transition={{
            duration: 3.5,
            delay: 0.6,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="flex h-16 w-16 flex-col items-center justify-center gap-1 rounded-2xl border border-white/60 bg-gradient-to-br from-white/80 via-white/60 to-brand-light/30 p-2 text-center shadow-xl shadow-black/10 backdrop-blur-xl backdrop-saturate-150 sm:h-20 sm:w-20 sm:gap-1.5 sm:p-4"
        >
          <Phone className="h-4 w-4 text-ink sm:h-5 sm:w-5" strokeWidth={1.75} />
          <p className="text-[10px] font-semibold text-ink sm:text-[11px]">Calls</p>
        </motion.div>
      </motion.div>

      {/* Floating dataset card — bottom right */}
      <motion.div
        initial={{ y: 10 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, delay: 1.2, ease: "easeOut" }}
        className="absolute -bottom-8 -right-2 z-20 sm:-bottom-10 sm:-right-4 lg:-bottom-12 lg:-right-6"
      >
        <motion.div
          animate={{ y: [0, -8, 0] }}
          transition={{
            duration: 4.5,
            delay: 0.3,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="w-52 rounded-2xl border border-white/60 bg-gradient-to-br from-white/85 to-brand-light/30 p-3 shadow-xl shadow-black/10 backdrop-blur-xl backdrop-saturate-150 sm:w-64 sm:p-3.5"
        >
          <div className="flex items-start gap-2">
            <img
              src="https://i.pravatar.cc/64?img=47"
              alt=""
              className="h-7 w-7 shrink-0 rounded-lg object-cover grayscale ring-1 ring-white/60 sm:h-8 sm:w-8"
            />
            <div>
              <p className="text-[10px] font-bold text-ink sm:text-[11px]">
                US Healthcare Organizations
              </p>
              <div className="mt-0.5 flex items-center gap-1.5">
                <span className="rounded-full bg-white/60 px-1.5 py-0.5 text-[8px] font-medium text-brand sm:text-[9px]">
                  Healthcare
                </span>
                <span className="text-[8px] text-ink-soft sm:text-[9px]">2.4M records</span>
              </div>
            </div>
          </div>
          <p className="mt-1.5 text-[9px] leading-snug text-ink-soft sm:mt-2 sm:text-[10px]">
            Verified database of 2.4M US healthcare organizations including
            hospitals, clinics, and medical practices with contact details.
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1 sm:mt-2">
            {["Hospitals", "Clinics", "NPI"].map((tag) => (
              <span
                key={tag}
                className="rounded-full border border-ink/15 px-1.5 py-0.5 text-[8px] font-medium text-ink-soft sm:px-2 sm:text-[9px]"
              >
                {tag}
              </span>
            ))}
          </div>
          <div className="my-2 border-t border-white/50" />
          <div className="flex items-end justify-between">
            <div>
              <div className="flex items-center gap-1 text-[9px] font-semibold text-ink sm:text-[10px]">
                <Star className="h-3 w-3 fill-brand text-brand" /> 4.9
                <span className="font-normal text-ink-soft/70">(312)</span>
              </div>
              <p className="mt-0.5 text-[8px] text-ink-soft sm:text-[9px]">
                Updated 2 days ago
              </p>
            </div>
            <div className="text-right">
              <p className="text-[11px] font-bold text-ink sm:text-xs">$99/mo</p>
              <button className="mt-0.5 rounded-full bg-gradient-to-br from-brand to-brand-dark px-2.5 py-0.5 text-[8px] font-medium text-white shadow-sm shadow-brand/30 sm:mt-1 sm:px-3 sm:py-1 sm:text-[9px]">
                Subscribe
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}
